// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsStyle } from "@tps/App/Components/Product/ProductActions";
import { CustomHasProductContext, withProductContext } from "@tps/App/Store/Contexts/CustomProductContext";
import LazyImage from "@tps/MobiusOverrides/LazyImage";
import ProductPrice, { ProductPriceStyles } from "@tps/Overrides/Components/ProductPrice";
import IconBadgeSpeedability from "@tps/XDL/Icons/IconBadgeSpeedability";
import { CustomWarehouseDto } from "@tps/XDL/types";
import { color, xScreen } from "@tps/XDL/Utilities";
import { mergeWithCss } from "@tps/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductShippingAndActionsProps {
    showShippingDropDown?: boolean;
    showAddToCartActions?: boolean;
    extendedStyles?: ProductShippingAndActionsStyle;
    onAddToCartClick?: () => void;
    showSpeedAbilityIcon?: boolean;
    showNumberInCart?: boolean;
}
const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    // xng-change
    const pageType: any = getCurrentPage(state).type;
    const isProductDetailsPage = pageType === "ProductDetailsPage";

    return {
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
        isAuthenticated: state.context.session.isAuthenticated && !state.context.session.isGuest,
        badgeOfSpeedability: (getSettingsCollection(state).customSettings as any).badgeOfSpeedability,
        defaultWarehouseName: JSON.parse(session.properties?.myBranch || "null")?.Name,
        // xng-change
        isProductDetailsPage,
    };
};

type Props = ProductShippingAndActionsProps & CustomHasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductShippingAndActionsStyle {
    productShippingAndActionsWrapper?: InjectableCss;
    productActionsStyles?: ProductActionsStyle;
    priceAvailabilityLink?: LinkPresentationProps;
    signInWrapper?: InjectableCss;
    productActionsWrapper?: InjectableCss;
    speedAbilityIconWrapper?: InjectableCss;
    shippingDropDown?: SelectPresentationProps;
    pleaseCallWrapper?: InjectableCss;
    pleaseCallButton?: ButtonPresentationProps;
}

const productShippingAndActionsStyle: ProductShippingAndActionsStyle = {
    priceAvailabilityLink: {
        typographyProps: {
            size: 12,
            lineHeight: "17px",
            color: "text.main",
            weight: "normal",
            css: css`
                &&& {
                    color: ${color("text.main")};
                }
            `,
        },
    },
    signInWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 4px;
        `,
    },
    productShippingAndActionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 12px;
            column-gap: 8px;
            ${xScreen("lte", "md")} {
                row-gap: 16px;
                flex-wrap: wrap;
            }
        `,
    },
    productActionsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-shrink: 0;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    speedAbilityIconWrapper: {
        css: css`
            width: 20px;
            min-width: 20px;
            display: flex;
            align-items: center;
            ${xScreen("lte", "md")} {
                align-items: end;
                margin-bottom: 4px;
            }
        `,
    },
    productActionsStyles: {
        addToCartContainer: {
            css: css`
                width: 100%;
            `,
        },
    },
    shippingDropDown: {
        cssOverrides: {
            formField: css`
                width: auto;
                flex-grow: 1;
                ${xScreen("lte", "md")} {
                    max-width: calc(100% - 28px);
                    flex-direction: column;
                    align-items: flex-start;
                }
            `,
            formInputWrapper: css`
                max-width: 297px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                    max-width: unset;
                }
            `,
        },
        labelProps: {
            css: css`
                ${xScreen("lte", "md")} {
                    padding: 0 0 8px;
                }
            `,
        },
    },
    pleaseCallWrapper: {
        css: css`
            min-width: 229px;
            display: flex;
            justify-content: flex-end;
            margin-left: auto;
            ${xScreen("lte", "md")} {
                width: 100%;
                justify-content: flex-start;
            }
        `,
    },
    pleaseCallButton: {
        css: css`
            min-width: 137px;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
};

const ProductShippingAndActions: FC<Props> = ({
    showShippingDropDown = true,
    extendedStyles,
    showAddToCartActions = true,
    canSeePrices,
    isAuthenticated,
    signInPageUrl,
    currentLocation,
    onAddToCartClick,
    showSpeedAbilityIcon,
    productContext: {
        product: { id: productId, properties },
        onWarehouseChanged,
        productInfo: { inventory, unitOfMeasure },
    },
    badgeOfSpeedability,
    showNumberInCart = false,
    defaultWarehouseName,
    // xng-change
    isProductDetailsPage,
}) => {
    const [styles] = useState(() => mergeWithCss(productShippingAndActionsStyle, extendedStyles));
    const [selectedWarehouse, setSelectedWarehouse] = useState<CustomWarehouseDto>();
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(properties.warehouseId || "");
    const [warehouseDtos, setWarehouseDtos] = useState<CustomWarehouseDto[]>();

    useEffect(() => {
        if (properties.warehouseId === selectedWarehouseId) {
            return;
        }
        setSelectedWarehouseId(properties.warehouseId);
        onWarehouseChanged && onWarehouseChanged(properties.warehouseId);
    }, [properties.warehouseId]);

    useEffect(() => {
        if (isAuthenticated) {
            // On first load, shipping drop down is taking first warehouseDto
            const warehouseDtos =
                (inventory?.inventoryWarehousesDtos?.find(
                    o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
                )?.warehouseDtos as CustomWarehouseDto[]) || undefined;

            if (!onWarehouseChanged) {
                logger.warn(`There was no onWarehouseChanged passed to the ProductContext for ${productId}`);
                return;
            }
            // we need to add the default warehouse even if its out off stock
            // due to comment above - functionality changed to remove default warehouse if it is OOS / TPS-1059
            const filteredWrehouses: CustomWarehouseDto[] = [];
            let defaultWarehouse: CustomWarehouseDto | undefined;
            if (warehouseDtos) {
                warehouseDtos.forEach(warehouse => {
                    if (warehouse.name === defaultWarehouseName && warehouse.qty > 0) {
                        defaultWarehouse = warehouse;
                    } else if (warehouse.qty > 0) {
                        filteredWrehouses.push(warehouse);
                    }
                });
                defaultWarehouse && filteredWrehouses.unshift(defaultWarehouse);
            }
            if (!filteredWrehouses || !filteredWrehouses.length) {
                return;
            }
            // if all warehouses are out of stock
            if (filteredWrehouses.length === 0) {
                return;
            }

            setWarehouseDtos(filteredWrehouses);
            setSelectedWarehouse(filteredWrehouses[0]);
            setSelectedWarehouseId(filteredWrehouses[0].properties["warehouseId"]);
            onWarehouseChanged(filteredWrehouses[0].properties["warehouseId"]);
        }
    }, [inventory, defaultWarehouseName]);

    const getSelectedWarehouse = (id: string) => {
        const warehouseSelected = warehouseDtos?.find(warehouse => warehouse.properties["warehouseId"] === id);
        return warehouseSelected;
    };

    const handleWrehouseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onWarehouseChanged) {
            logger.warn(`There was no onWarehouseChanged passed to the ProductContext for ${productId}`);
            return;
        }
        const value = event.currentTarget.value;
        const warehouseSelected = getSelectedWarehouse(value);
        setSelectedWarehouse(warehouseSelected);
        setSelectedWarehouseId(value);
        onWarehouseChanged(value);
    };

    if (!isAuthenticated) {
        const returnUrl = currentLocation.pathname + currentLocation.search;
        return (
            <StyledWrapper {...styles.signInWrapper}>
                <Link
                    // xng-change
                    // href={`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`}
                    href={`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}${
                        isProductDetailsPage ? "&isForceRedirect=true" : ""
                    }`}
                    {...styles.priceAvailabilityLink}
                >
                    {siteMessage("Availability_SignInForAvailability")}
                </Link>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.productShippingAndActionsWrapper}>
            {canSeePrices && inventory && inventory?.qtyOnHand > 0 && (
                <>
                    {showShippingDropDown && (
                        <>
                            {warehouseDtos && (
                                <Select
                                    {...styles.shippingDropDown}
                                    sizeVariant="small"
                                    label={translate("Ships From:")}
                                    labelPosition="left"
                                    onChange={handleWrehouseChange}
                                    // value={selectedWarehouse?.properties.warehouseId}
                                    value={selectedWarehouseId}
                                    data-test-selector="product_shippingInfo"
                                >
                                    {warehouseDtos.map(warehouse => (
                                        <option
                                            key={warehouse.properties.warehouseId}
                                            value={warehouse.properties.warehouseId}
                                        >
                                            {warehouse.message}
                                        </option>
                                    ))}
                                </Select>
                            )}
                            {showSpeedAbilityIcon && (
                                <StyledWrapper {...styles.speedAbilityIconWrapper}>
                                    {selectedWarehouse &&
                                        selectedWarehouse.properties.speedability?.toLowerCase() === "true" && (
                                            <Img
                                                src={badgeOfSpeedability}
                                                altText="Speedability"
                                                width={20}
                                                height={20}
                                            />
                                        )}
                                </StyledWrapper>
                            )}
                        </>
                    )}

                    {showAddToCartActions && (
                        <StyledWrapper {...styles.productActionsWrapper}>
                            <ProductActions
                                showNumberInCart={showNumberInCart}
                                onAddToCartClick={onAddToCartClick}
                                extendedStyles={styles.productActionsStyles}
                            />
                        </StyledWrapper>
                    )}
                </>
            )}

            {(!canSeePrices || !inventory || inventory?.qtyOnHand === 0) && (
                <StyledWrapper {...styles.pleaseCallWrapper}>
                    <Button
                        aria-label={translate("Please Call")}
                        disabled={true}
                        {...styles.pleaseCallButton}
                        sizeVariant="small"
                    >
                        {translate("Please Call")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductShippingAndActions));
